import React from 'react';
import { FiX } from 'react-icons/fi';

/**
 * Compact rounded chip — matches the design's `.pill` recipe (3px 10px,
 * 999px radius, 600/11/16 type). Used for status indicators, tags, filter
 * chips, and the workout-flow chips throughout the mobile/web UX.
 *
 * Variants:    primary | neutral | success | warning | error | cyan | brand
 * Appearance:  filled (default — solid soft-tinted background) | outline
 * Sizes:       sm | md (default)
 *
 * `dot` adds the design's leading bullet (currentColor circle, 6px).
 * `dismissible` + `onDismiss` add a close button.
 * `interactive` (or passing `onClick`) renders a `<button>` with hover/focus
 * styles for clickable filter chips.
 */
const FILLED = {
  primary: 'bg-primary-soft text-primary border-transparent',
  neutral: 'bg-soft text-fg-muted border-border-soft',
  success: 'bg-success-soft text-success border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  error: 'bg-error-soft text-error border-transparent',
  cyan: 'bg-secondary-soft text-secondary border-transparent',
  brand: 'bg-[rgba(10,126,164,0.12)] text-brand border-transparent',
};

const OUTLINE = {
  primary: 'bg-transparent text-primary border-primary/40',
  neutral: 'bg-transparent text-fg-muted border-border-default',
  success: 'bg-transparent text-success border-[rgba(16,185,129,0.4)]',
  warning: 'bg-transparent text-warning border-[rgba(245,158,11,0.4)]',
  error: 'bg-transparent text-error border-[rgba(239,68,68,0.4)]',
  cyan: 'bg-transparent text-secondary border-[rgba(6,182,212,0.4)]',
  brand: 'bg-transparent text-brand border-[rgba(10,126,164,0.4)]',
};

const SIZES = {
  // Spec: padding 3px 10px, 600 11/16 — i.e. h-[22px]. Approximated.
  sm: 'h-5 px-2 text-[10px] gap-1',
  md: 'h-[22px] px-2.5 text-[11px] leading-4 gap-1.5',
};

const ICON_SIZE = {
  sm: 'w-3 h-3',
  md: 'w-3 h-3',
};

export default function Pill({
  variant = 'neutral',
  appearance = 'filled',
  size = 'md',
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  dot = false,
  dismissible = false,
  onDismiss,
  interactive = false,
  onClick,
  className = '',
  children,
  ...rest
}) {
  const palette = appearance === 'outline' ? OUTLINE : FILLED;
  const Wrapper = interactive || onClick ? 'button' : 'span';
  const isButton = Wrapper === 'button';

  const classes = [
    'inline-flex items-center font-semibold rounded-pill border whitespace-nowrap',
    palette[variant] ?? palette.neutral,
    SIZES[size] ?? SIZES.md,
    isButton
      ? 'transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconClass = ICON_SIZE[size] ?? ICON_SIZE.md;

  return (
    <Wrapper
      className={classes}
      onClick={onClick}
      type={isButton ? 'button' : undefined}
      {...rest}
    >
      {dot ? (
        <span
          className="inline-block w-1.5 h-1.5 rounded-full bg-current"
          aria-hidden="true"
        />
      ) : null}
      {LeadingIcon ? <LeadingIcon className={iconClass} /> : null}
      <span>{children}</span>
      {TrailingIcon ? <TrailingIcon className={iconClass} /> : null}
      {dismissible ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.(e);
          }}
          aria-label="Remove"
          className="ml-0.5 -mr-1 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/15"
        >
          <FiX className={iconClass} />
        </button>
      ) : null}
    </Wrapper>
  );
}
